import { Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { Link, OutlineCard } from '@morressier/alembic';
import { ISubmissionContext } from '@publishing-engine/domain/models';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import EditLogoModal from '../JournalConfig/EditLogoModal';
import { IFileUploaded } from 'src/components/FormField/common/types';
import GeneralInfoEditableSection from './GeneralInfoEditableSection';
import {
  GENERAL_INFO_EDITABLE_FIELD_MAP,
  getDisplayValue,
  getPublishingModelDisplayValue,
  withJournalActiveLock
} from './generalInfoConfig';
import JournalEmailsSection from './JournalEmailsSection';

interface Props {
  journal: ISubmissionContext;
  isActive: boolean| undefined;
}

type LogoForm = {
  config?: {
    logoUrl?: string;
  };
  logoFile?: IFileUploaded | null;
};

export const GeneralInfo: React.FC<Props> = ({ journal, isActive }) => {
  const logoMethods = useForm<LogoForm>();
  const { reset: resetLogo } = logoMethods;

  useEffect(() => {
    const logoUrl = journal.config?.logoUrl;
    resetLogo({
      config: { logoUrl: logoUrl ?? '' },
      logoFile: logoUrl ? { url: logoUrl } : null
    });
  }, [journal, resetLogo]);

  return (
    <Flex flexDirection={'column'} gap={6}>
      <Text textStyle={'headingSection'}>General Info</Text>
      <OutlineCard borderless paddingX={4} paddingY={6}>
        <Grid
          templateColumns="repeat(2, 1fr)"
          gap={6}
          flex="1"
        >
          <GridItem>
            <GeneralInfoEditableSection
              label="Journal Name"
              value={<Text>{getDisplayValue(journal.data?.name)}</Text>}
              journal={journal}
              fieldConfig={withJournalActiveLock(
                GENERAL_INFO_EDITABLE_FIELD_MAP.name,
                isActive
              )}
            />
          </GridItem>
          <GridItem>
            <GeneralInfoEditableSection
              label="Journal Code"
              value={<Text>{getDisplayValue(journal.data?.shortname).toUpperCase()}</Text>}
              journal={journal}
              fieldConfig={withJournalActiveLock(
                GENERAL_INFO_EDITABLE_FIELD_MAP.shortname,
                isActive
              )}
            />
          </GridItem>
          <GridItem>
            <GeneralInfoEditableSection
              label="Journal Description"
              value={<Text>{getDisplayValue(journal.data?.description)}</Text>}
              journal={journal}
              fieldConfig={GENERAL_INFO_EDITABLE_FIELD_MAP.description}
            />
          </GridItem>
          <GridItem>
            <GeneralInfoEditableSection
              label="Journal Introduction"
              value={<Text>{getDisplayValue(journal.data?.introduction)}</Text>}
              journal={journal}
              fieldConfig={GENERAL_INFO_EDITABLE_FIELD_MAP.introduction}
            />
          </GridItem>
          <GridItem>
            <GeneralInfoEditableSection
              label="Journal URL"
              value={
                journal.config?.url ? (
                  <Link variant="primary" href={journal.config.url} target="_blank">
                    {journal.config.url}
                  </Link>
                ) : (
                  <Text textStyle={'textBody'}>No data available</Text>
                )
              }
              journal={journal}
              fieldConfig={GENERAL_INFO_EDITABLE_FIELD_MAP.url}
            />
          </GridItem>
          <GridItem>
            <JournalEmailsSection journal={journal} />
          </GridItem>
          <GridItem>
            <GeneralInfoEditableSection
              label="ISSN (Print)"
              value={<Text>{getDisplayValue(journal.config?.issn?.print)}</Text>}
              journal={journal}
              fieldConfig={GENERAL_INFO_EDITABLE_FIELD_MAP.issnPrint}
            />
          </GridItem>
          <GridItem>
            <GeneralInfoEditableSection
              label="ISSN (Online)"
              value={<Text>{getDisplayValue(journal.config?.issn?.online)}</Text>}
              journal={journal}
              fieldConfig={GENERAL_INFO_EDITABLE_FIELD_MAP.issnOnline}
            />
          </GridItem>
          <GridItem>
            <GeneralInfoEditableSection
              label="Publishing Model"
              value={<Text>{getPublishingModelDisplayValue(journal)}</Text>}
              journal={journal}
              fieldConfig={GENERAL_INFO_EDITABLE_FIELD_MAP.publishingModel}
            />
          </GridItem>
          <GridItem>
            <FormProvider {...logoMethods}>
              <EditLogoModal
                journalId={journal.id}
                organizationId={journal.organizationId}
              />
            </FormProvider>
          </GridItem>
        </Grid>
      </OutlineCard>
    </Flex>
  );
};
